import { useState, useEffect } from 'react';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Hook to check whether a feature flag is enabled for the current user.
 * - Prefers per-tier feature settings (user.tier.features)
 * - Falls back to admin feature flag endpoint when not present
 */
export function useFeatureGate(key) {
  // Attempt to read auth context; tests sometimes render components
  // without an AuthProvider, so guard against the hook throwing.
  let user = null;
  try {
    const auth = useAuth();
    user = auth?.user ?? null;
  } catch (e) {
    user = null;
  }

  // Map legacy flat keys to nested paths for backwards compatibility
  const tierFeatures = user?.tier?.features || {};
  const featurePathMap = {
    'export_enabled': tierFeatures?.export?.enabled,
    'collection_enabled': tierFeatures?.collection?.enabled,
  };
  
  const tierHasKey = featurePathMap[key] !== undefined || Object.prototype.hasOwnProperty.call(tierFeatures, key);

  const [enabled, setEnabled] = useState(() => {
    // Check mapped path first, then fallback to direct key
    if (featurePathMap[key] !== undefined) return !!featurePathMap[key];
    if (tierHasKey) return !!tierFeatures[key];
    // Default behaviour: most flags are optimistic-allow, but payment
    // gating must be fail-closed so the UI doesn't show billing when
    // the backend or admin flag is unavailable. Keep optimistic-allow
    // for other flags to avoid blocking unrelated flows.
    // For `stripe_payments` default to null (unknown) until we
    // explicitly fetch an enabled flag from the admin endpoint.
    // Pages may treat null as permissive where appropriate.
    if (key === 'stripe_payments') return null; // unknown until we fetch
    return true;
  });

  useEffect(() => {
    let mounted = true;

    if (featurePathMap[key] !== undefined) {
      setEnabled(!!featurePathMap[key]);
      return () => { mounted = false; };
    }
    
    if (tierHasKey) {
      setEnabled(!!tierFeatures[key]);
      return () => { mounted = false; };
    }

    // Try to fetch admin feature flag; on error leave current value
    async function fetchFlag() {
      try {
        const res = await api.getFeatureFlag(key);
        if (!mounted) return;
        if (res && res.flag && typeof res.flag.enabled !== 'undefined') {
          setEnabled(!!res.flag.enabled);
        }
      } catch (e) {
        // On error: for critical flags like payments, fail-closed.
        /* c8 ignore start */
        if (key === 'stripe_payments') {
          if (mounted) setEnabled(false);
        }
        /* c8 ignore stop */
        // For other flags, ignore errors and keep optimistic value
      }
    }

    fetchFlag();

    return () => { mounted = false; };
  }, [key, tierHasKey, tierFeatures]);

  return enabled;
}

export default useFeatureGate;
